import { useEffect, useMemo, useState } from 'react';

export type PaginationOptions = {
  defaultSize?: number;
  totalCount?: number;
  totalPages?: number;
};

export const usePagination = (options?: PaginationOptions) => {
  const defaultSize = options?.defaultSize ?? 20;
  const totalCount = options?.totalCount;
  const totalPagesOverride = options?.totalPages;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultSize);

  const totalPages = useMemo(() => {
    if (typeof totalPagesOverride === 'number') return totalPagesOverride;
    if (typeof totalCount !== 'number') return undefined;
    return Math.ceil(totalCount / pageSize);
  }, [totalPagesOverride, totalCount, pageSize]);

  const handleNextPage = () => {
    if (typeof totalPages === 'number' && currentPage >= totalPages) return;
    setCurrentPage((cur) => cur + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage <= 1) return;
    setCurrentPage((cur) => cur - 1);
  };

  const canNextPage =
    typeof totalPages === 'number' ? currentPage < totalPages : false;
  const canPreviousPage = currentPage > 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const resetCurrentPage = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    totalPages,
    pageSize,
    handleNextPage,
    handlePreviousPage,
    setPageSize,
    canNextPage,
    canPreviousPage,
    resetCurrentPage,
  };
};
